import { useCallback, useEffect, useState } from "react";
import { SessionTimeout } from "../types/SessionTimeout.type";
import { SessionEvent, useSessionBroadcast } from "./useSessionChannel";
import { getOrFetchXsrfToken } from "../utils";

// routes that should not trigger the session timeout
const ExcludedRoutes = [
	"/",
	"/login",
	"/member/login",
	"/verify-email",
	"/verification",
	"/guest/login",
	"/guest/signup",
	"/two-factor",
	"/thank-you",
	"/two-factor-setup",
];

const SESSION_WARNING_THRESHOLD = 2 * 60 * 1000; // 2 minutes before expiry in ms

/**
 *
 * @returns
 */
const useSessionTimeout = (): SessionTimeout => {
	const [expiryTime, setExpiryTime] = useState<number | null>(null);
	const [warningThreshold, setWarningThreshold] = useState<number>(
		SESSION_WARNING_THRESHOLD
	);
	const { sendSessionBroadcast } = useSessionBroadcast((msg) => {
		switch (msg.type) {
			case SessionEvent.SESSION_UPDATE: {
				setExpiryTime(msg.timestamp ?? 0);
			}
		}
	});

	const isExcludedRoute = () => {
		return ExcludedRoutes.some((route) => window?.location?.pathname === route);
	};

	const timeLeftMs = () => {
		return expiryTime ? expiryTime - Date.now() : 0;
	};

	/**
	 *
	 * @param expiresAt timestamp for expiry in milliseconds
	 */
	const updateSessionExpiry = useCallback(
		(expiresAt: number) => {
			// Broadcast to other tabs
			sendSessionBroadcast(SessionEvent.SESSION_UPDATE, expiresAt);

			// Update current tab
			setExpiryTime(expiresAt);
		},
		[sendSessionBroadcast]
	);

	useEffect(() => {
		window.__PROCURACON_SESSION_HOOK_UPDATE__ = updateSessionExpiry;
		return () => {
			delete window.__PROCURACON_SESSION_HOOK_UPDATE__;
		};
	}, [updateSessionExpiry]);

	return {
		expiryTime,
		warningThreshold,
		extendSession: getOrFetchXsrfToken,
		isExcludedRoute,
		setWarningThreshold,
		timeLeftMs,
		updateSessionExpiry,
	};
};

export default useSessionTimeout;
